/**
 * AR Collections API Service
 *
 **/
import { api } from '@xtnd/web/shared/api';
import { FileCategory } from '../types/matter';
import { ApiResponse, QueryStringParams } from '@xtnd/web/shared/types';
import { fileUtil } from '@xtnd/web/shared';
import queryString from 'query-string';
import { isUndefined, isPlainObject, isEmpty, omitBy } from 'lodash';
import { ArCollectionQueueFilter, ArCollectionQueueParams, ArCollectionQueueResponse, ArMattersResponse, SORT_BY } from '../types';

class ArCollectionApiService {
  private readonly baseResource = '/ar-collections';

  /**
   * Get collection queue with pagination
   */
  async getCollectionQueue(params: QueryStringParams): Promise<ArCollectionQueueResponse> {
    const cleanupParams = omitBy(params, (value) =>
      isUndefined(value) || (isPlainObject(value) && isEmpty(value))
    );

    const filters: ArCollectionQueueFilter | undefined = cleanupParams.filters as ArCollectionQueueFilter;
    const sorts = cleanupParams.sorts as Record<SORT_BY, 'asc' | 'desc'>;
    const queryParams: Record<string, any> = {
      ...(cleanupParams.limit && { limit: cleanupParams.limit }),
      ...(cleanupParams.page && { page: cleanupParams.page }),
      ...(filters?.groupBy && { groupBy: filters.groupBy }),
      ...(filters?.search && { search: filters.search }),
      ...(filters?.daysPastDue !== undefined && { daysPastDue: filters.daysPastDue }),
    };

    // sorting by 1 field
    if (sorts) {
      const values = (Object.entries(sorts) as [SORT_BY, 'asc' | 'desc' | undefined][])
        .find(([, order]) => order !== undefined);

      if (values) {
        const [sortBy, order] = values;
        queryParams.sortBy = sortBy;
        queryParams.order = order;
      }
    }

    const query = queryString.stringify(queryParams);
    const endpoint = `${this.baseResource}/queue${query ? `?${query}` : ''}`;

    return await api.get<ArCollectionQueueResponse>(endpoint);
  }

  /**
   * Get matters by entity Id
   */
  async getMatters(params: ArCollectionQueueParams): Promise<ArMattersResponse> {
    const cleanupParams = omitBy(params, (value) =>
      isUndefined(value) || (isPlainObject(value) && isEmpty(value))
    );

    const filters: ArCollectionQueueFilter | undefined = cleanupParams.filters as ArCollectionQueueFilter;
    const queryParams: Record<string, any> = {
      ...(filters?.groupBy && { groupBy: filters.groupBy }),
      ...(filters?.daysPastDue !== undefined && { daysPastDue: filters.daysPastDue }),
      ...(filters?.entityId && { entityId: filters.entityId }),
      ...(filters?.clientId && { clientId: filters.clientId }),
    };

    if (!queryParams.entityId) {
      return { data: [] } as unknown as ArMattersResponse;
    }

    const query = queryString.stringify(queryParams);
    const endpoint = `${this.baseResource}/queue${query ? `?${query}` : ''}`;

    return await api.get<ArMattersResponse>(endpoint);
  }

  /**
   * Export collection queue
   */
  async exportCollection(params: QueryStringParams): Promise<any> {
    const cleanupParams = omitBy(params, (value) =>
      isUndefined(value) || (isPlainObject(value) && isEmpty(value))
    );

    const filters: ArCollectionQueueFilter | undefined = cleanupParams.filters as ArCollectionQueueFilter;
    const sorts = cleanupParams.sorts as Record<SORT_BY, 'asc' | 'desc'>;
    const queryParams: Record<string, any> = {
      ...(cleanupParams.limit && { limit: cleanupParams.limit }),
      ...(cleanupParams.page && { page: cleanupParams.page }),
      ...(filters?.groupBy && { groupBy: filters.groupBy }),
      ...(filters?.search && { search: filters.search }),
      ...(filters?.daysPastDue !== undefined && { daysPastDue: filters.daysPastDue }),
      ...(filters?.entityId && { entityId: filters.entityId }),
      ...(filters?.clientId && { clientId: filters.clientId }),
      ...(filters?.matterGroupBy && { matterGroupBy: filters.matterGroupBy }),
      ...(filters?.exportType && { exportType: filters.exportType }),
      ...(filters?.exportMeta && { exportMeta: JSON.stringify(filters.exportMeta) }),
      ...(filters?.childSearch && { childSearch: filters.childSearch }),
      ...(filters?.grandChildSearch && { grandChildSearch: filters.grandChildSearch }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // sorting by 1 field
    if (sorts) {
      const values = (Object.entries(sorts) as [SORT_BY, 'asc' | 'desc' | undefined][])
        .find(([, order]) => order !== undefined);

      if (values) {
        const [sortBy, order] = values;
        queryParams.sortBy = sortBy;
        queryParams.order = order;
      }
    }

    const query = queryString.stringify(queryParams);
    const endpoint = `${this.baseResource}/queue/export${query ? `?${query}` : ''}`;

    const fileData = await api.get<Blob>(endpoint);

    const base64 = await fileData.text();

    const exportFilename = filters?.exportFilename || `collection-groupBy-${filters?.groupBy}`;

    await fileUtil.downloadBase64(
      base64,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      exportFilename
    );

    return 'success';
  }

  /**
   * Export collection queue as PDF and open in a new browser tab.
   * Throws Error('POPUP_BLOCKED') if the browser blocks the new tab.
   */
  async exportCollectionPdf(params: QueryStringParams): Promise<void> {
    const cleanupParams = omitBy(params, (value) =>
      isUndefined(value) || (isPlainObject(value) && isEmpty(value))
    );

    const filters: ArCollectionQueueFilter | undefined = cleanupParams.filters as ArCollectionQueueFilter;
    const sorts = cleanupParams.sorts as Record<SORT_BY, 'asc' | 'desc'>;
    const queryParams: Record<string, any> = {
      ...(cleanupParams.limit && { limit: cleanupParams.limit }),
      ...(cleanupParams.page && { page: cleanupParams.page }),
      ...(filters?.groupBy && { groupBy: filters.groupBy }),
      ...(filters?.search && { search: filters.search }),
      ...(filters?.daysPastDue !== undefined && { daysPastDue: filters.daysPastDue }),
      ...(filters?.entityId && { entityId: filters.entityId }),
      ...(filters?.clientId && { clientId: filters.clientId }),
      ...(filters?.matterGroupBy && { matterGroupBy: filters.matterGroupBy }),
      ...(filters?.exportType !== undefined && { exportType: filters.exportType }),
      ...(filters?.exportMeta && { exportMeta: JSON.stringify(filters.exportMeta) }),
      ...(filters?.childSearch && { childSearch: filters.childSearch }),
      ...(filters?.grandChildSearch && { grandChildSearch: filters.grandChildSearch }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (sorts) {
      const values = (Object.entries(sorts) as [SORT_BY, 'asc' | 'desc' | undefined][])
        .find(([, order]) => order !== undefined);
      if (values) {
        const [sortBy, order] = values;
        queryParams.sortBy = sortBy;
        queryParams.order = order;
      }
    }

    const query = queryString.stringify(queryParams);
    const endpoint = `${this.baseResource}/queue/export${query ? `?${query}` : ''}`;

    // Endpoint returns a signed S3 download URL, not the PDF bytes: a
    // consolidated report inlined as base64 can exceed Lambda's ~6MB
    // synchronous response limit. Open the URL directly so the browser
    // fetches the file straight from S3 - no size ceiling on that path.
    const response = await api.get<{ downloadUrl: string; expiresAt: string }>(endpoint);
    const tab = window.open(response.downloadUrl, '_blank');
    if (!tab) {
      throw new Error('POPUP_BLOCKED');
    }
  }

  async getSummaryFileSignUrl(payload: { name: string, size: number, type: string, category: FileCategory }): Promise<ApiResponse<{ key: string, signUrl: string }>> {
    const endpoint = `/ar-collections/sign-url`;
    return await api.post<ApiResponse<{ key: string, signUrl: string }>>(endpoint, payload);
  }

  async getPreviewFile(key: string, category?: string): Promise<ApiResponse<string>> {
    const endpoint = `/ar-collections/preview/${encodeURIComponent(key)}?category=${category}`;
    return await api.get<ApiResponse<string>>(endpoint);
  }
}


// Export singleton instance
export const arCollectionQueueApi = new ArCollectionApiService();

